import React from "react";
import { CheckCircle2 } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { assetUrls } from "@/data/products";

const principles = [
  {
    title: "No synthetic pesticides",
    copy: "We rely on companion planting, crop rotation and natural pest management instead of synthetic sprays.",
  },
  {
    title: "No artificial growth inputs",
    copy: "No synthetic fertilisers or growth promoters. Soil character is built with compost, observation and time.",
  },
  {
    title: "Seasonal by design",
    copy: "We grow what the hills grow when they grow it. Out-of-season is not on our table.",
  },
  {
    title: "Soil first",
    copy: "Living soil makes honest food. We measure success in topsoil, not just tonnage.",
  },
  {
    title: "Local seed where possible",
    copy: "We work with traditional hill varieties and small seed savers in the region whenever possible.",
  },
  {
    title: "Honest labels",
    copy: "We do not claim organic certification unless certified. We say naturally grown and mean it.",
  },
];

export default function NaturalFarming() {
  return (
    <main className="bg-[#FAFAF8] pt-32" data-testid="natural-farming-page">
      <section className="relative -mt-32 min-h-[70vh] overflow-hidden pt-32" data-testid="natural-hero-section">
        <img data-testid="natural-hero-background" src={assetUrls.hillside} alt="Misty hillside farm" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#FAFAF8]/70 backdrop-blur-[1px]" />
        <div className="relative mx-auto flex min-h-[70vh] max-w-7xl items-end px-6 pb-20 pt-32 md:px-10 md:pb-28">
          <div className="max-w-5xl">
            <p data-testid="natural-hero-eyebrow" className="mb-5 text-xs font-semibold uppercase tracking-[0.22em] text-[#2A4B3C]">Natural Farming</p>
            <h1 data-testid="natural-hero-heading" className="text-5xl font-light tracking-tight text-[#151512] sm:text-6xl lg:text-7xl">Nothing artificial. Just nature, patience and mountain soil.</h1>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="natural-principles-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <SectionHeading eyebrow="How we grow" title="Six principles we don’t break." testId="natural-principles-heading" />
          <div className="grid gap-5 md:grid-cols-3">
            {principles.map((principle, index) => (
              <article data-testid={`principle-card-${index + 1}`} key={principle.title} className="rounded-[2rem] border border-[#EAEAE6] bg-[#FAFAF8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <p data-testid={`principle-number-${index + 1}`} className="text-6xl font-light text-[#D5D5D0]">{String(index + 1).padStart(2, "0")}</p>
                <h3 data-testid={`principle-title-${index + 1}`} className="mt-8 text-2xl font-normal tracking-tight text-[#2C2C2A]">{principle.title}</h3>
                <p data-testid={`principle-copy-${index + 1}`} className="mt-4 leading-relaxed text-[#6B6B66]">{principle.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-12 md:px-10 md:py-32" data-testid="natural-soil-patience-section">
        <div className="overflow-hidden rounded-[2.25rem] md:col-span-5">
          <img data-testid="natural-soil-image" src={assetUrls.naturalFarming} alt="Hands holding soil and a young sprout" className="h-full min-h-[520px] w-full object-cover" />
        </div>
        <div className="flex flex-col justify-center space-y-7 text-xl leading-relaxed text-[#6B6B66] md:col-span-7" data-testid="natural-soil-copy">
          <p>Our produce is grown without synthetic pesticides or artificial growth inputs. Instead, we lean on the things that worked in these hills for centuries — compost, crop rotation, careful timing and patience.</p>
          <p>This is slower farming. The yields are smaller. The seasons are real. But the flavour is honest, the soil keeps getting better year by year, and the farmers who tend these fields can keep doing so for generations.</p>
          <div className="grid gap-4 pt-4 sm:grid-cols-2">
            {["Compost and time", "Crop rotation", "Careful timing", "Seasonal patience"].map((item) => (
              <div data-testid={`natural-soil-note-${item.toLowerCase().replaceAll(" ", "-")}`} key={item} className="flex items-center gap-3 rounded-full bg-white px-5 py-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <CheckCircle2 className="h-5 w-5 text-[#4F795B]" />
                <span className="text-base font-medium text-[#2C2C2A]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}